import React from 'react'
import type { Podcast } from '../types/podcast'
import { PodcastStatusEnum } from '../types/enums'
import { humanizeDate } from '../lib/date-utils'
import { formatDuration } from '../lib/utils'
import StatusBadge from './ui/status-badge'
import Button from './ui/button'
import Tooltip from './ui/tooltip'
import { Clock, Download, Trash2, Clipboard, Check, Bug, FileJson } from 'lucide-react'
import { AnimatedIconButton } from './ui/animated-icon-button'
import { useNavigate } from 'react-router-dom'
import { downloadFile, sanitizeFilename } from '../lib/file-download'
import { markPodcastPlayedForCurrentUser } from '../api/podcasts'
import { removeRecentEpisode } from '../lib/recent-episodes'
import { copyMarketingPodcastJsonToClipboard, buildMarketingPodcastJson } from '../lib/generate-marketing-podcast-json'
import { useAuth } from '../hooks/use-auth'
import UpgradeDialog from './billing/upgrade-dialog'

export interface PodcastCardProps {
  podcast: Podcast
  onDelete?: (podcastId: string) => void
  onRefetch?: () => Promise<void>
  // When true, hides the delete button in embedded contexts.
  hideDelete?: boolean
}

const PodcastCard: React.FC<PodcastCardProps> = ({ podcast, onDelete, onRefetch, hideDelete = false }) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [devState, setDevState] = React.useState<'idle' | 'working' | 'success' | 'error'>('idle')
  const [devMessage, setDevMessage] = React.useState<string>('')
  const [showDownloadPaywall, setShowDownloadPaywall] = React.useState(false)

  const title = podcast.title || 'Untitled podcast'
  const desc = (podcast.description || '').trim()
  const titleId = `podcast-${podcast.id}`
  const canDownload = podcast.status === PodcastStatusEnum.Completed && !!podcast.audioFileUrl
  const displayStatus = podcast.isGenerating ? PodcastStatusEnum.Processing : podcast.status

  const handleCardClick = () => {
    navigate(`/podcasts/${podcast.id}`)
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const isPaidUser = (user?.plan ?? 'free') !== 'free'
    if (!isPaidUser) {
      setShowDownloadPaywall(true)
      return
    }
    if (podcast.audioFileUrl) {
      const safeTitle = sanitizeFilename(podcast.title || 'podcast')
      void downloadFile(podcast.audioFileUrl, `${safeTitle}.mp3`).then((ok) => {
        if (ok) {
          void markPodcastPlayedForCurrentUser(podcast.id).catch(() => { /* noop */ })
        }
      })
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      removeRecentEpisode(podcast.id)
      onDelete?.(podcast.id)
      await onRefetch?.()
    } catch (error) {
      console.error('Failed to delete podcast:', error)
    }
  }

  const handleDevJson = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (import.meta.env.MODE === 'production') return
    
    setDevState('working')
    try {
      const eligibility = buildMarketingPodcastJson(podcast)
      if (!eligibility.ok) {
        setDevState('error')
        setDevMessage(eligibility.reasonIneligible || '')
        setTimeout(() => setDevState('idle'), 3500)
        return
      }
      await copyMarketingPodcastJsonToClipboard(podcast)
      setDevState('success')
      setDevMessage(`Copied ${eligibility.slug}.json`)
    } catch (err) {
      setDevState('error')
      setDevMessage(err instanceof Error ? err.message : 'Failed')
    } finally {
      setTimeout(() => setDevState('idle'), 4000)
    }
  }

  const eligibility = import.meta.env.MODE !== 'production' ? buildMarketingPodcastJson(podcast) : null
  const disabledReason = eligibility && !eligibility.ok ? eligibility.reasonIneligible : undefined

  return (
    <article 
      className="group bg-white rounded-xl border border-black/5 p-5 hover:shadow-sm transition-shadow flex flex-col h-full cursor-pointer"
      aria-labelledby={titleId}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-1">
        <div className="min-w-0 flex-1">
          <h3 id={titleId} className="truncate font-medium text-foreground" title={title}>
            {title}
          </h3>
          {desc ? (
            <p className="mt-1 line-clamp-2 text-sm text-foreground/70" title={desc}>{desc}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge status={displayStatus} />
            {podcast.durationSeconds && (
              <>
                <span className="text-foreground/50 hidden sm:inline">·</span>
                <span className="text-foreground/60 inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDuration(podcast.durationSeconds)}
                </span>
              </>
            )}
            <span className="text-foreground/50 hidden sm:inline">·</span>
            <span className="text-foreground/60">Updated {humanizeDate(podcast.updatedAt)}</span>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigate(`/podcasts/${podcast.id}`)
              }}
            >
              Open
            </Button>
            {import.meta.env.MODE !== 'production' && (
              <Tooltip content={disabledReason ? `Dev JSON: ${disabledReason}` : (devMessage || 'Copy JSON for marketing site')}> 
                <button
                  type="button"
                  onClick={handleDevJson}
                  className={`cursor-pointer inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors shadow-sm
                    ${disabledReason ? 'bg-foreground/5 text-foreground/40 ring-foreground/10 cursor-not-allowed' : 'bg-warning-600 hover:bg-warning-700 text-white ring-black/10'}
                  `}
                  disabled={!!disabledReason || devState === 'working'}
                  aria-label="Copy marketing JSON"
                >
                  {devState === 'working' ? <Clipboard className="h-3.5 w-3.5 animate-pulse" /> : devState === 'success' ? <Check className="h-3.5 w-3.5" /> : devState === 'error' ? <Bug className="h-3.5 w-3.5" /> : <FileJson className="h-3.5 w-3.5" />}
                  {devState === 'working' ? 'Generating…' : devState === 'success' ? 'Copied' : devState === 'error' ? 'Retry' : 'Dev JSON'}
                </button>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-1">
            {canDownload ? (
              <Tooltip content="Download audio">
                <AnimatedIconButton
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="text-foreground/60 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Download podcast audio"
                  icon={<Download className="h-4 w-4" />}
                />
              </Tooltip>
            ) : null}
            {!hideDelete && (
              <Tooltip content="Delete podcast">
                <AnimatedIconButton
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete podcast"
                  icon={<Trash2 className="h-4 w-4" />}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Paywall: download episode */}
      <UpgradeDialog
        open={showDownloadPaywall}
        onOpenChange={setShowDownloadPaywall}
        title="Keep this episode forever"
        description={undefined}
        bullets={[
          "High-quality MP3 export",
          "Keep files forever, even if you cancel",
          "Works offline in any podcast app",
          "Archive your best episodes"
        ]}
        paywallId="download_episode"
      />
    </article>
  )
}

export default PodcastCard
